import { useState, useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { publicApi } from "../lib/api";
import Reveal from "../components/Reveal";
import Seo from "../components/Seo";

export default function DestinationList() {
  const [params] = useSearchParams();
  const initialRegion = params.get("region") || "All";
  const [filter, setFilter] = useState(initialRegion);
  const [destinations, setDestinations] = useState([]);

  useEffect(() => {
    publicApi.get("/destinations").then((r) => setDestinations(r.data)).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    if (filter === "India") return destinations.filter((d) => d.category === "inbound");
    if (filter === "International") return destinations.filter((d) => d.category === "outbound");
    return destinations;
  }, [filter, destinations]);

  return (
    <div>
      <Seo
        title="Luxury Destinations — India & International · Voyage Luxe"
        description="Thirteen curated luxury destinations — Rajasthan, Kerala, Golden Triangle, Maldives, Switzerland, Japan, Bhutan, Sri Lanka, Africa & more. Private bespoke itineraries."
      />
      {/* Hero */}
      <section className="pt-40 md:pt-48 pb-16 md:pb-20 container-luxe">
        <div className="eyebrow mb-6">Destinations</div>
        <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight text-balance max-w-5xl">
          Every journey begins<br /><span className="italic">with a place to imagine</span>.
        </h1>
        <p className="mt-8 text-foreground/70 max-w-2xl leading-relaxed">
          Thirteen cinematic destinations — five within India, eight beyond — each curated by our local atelier teams and refined over a decade of private hosting.
        </p>

        <div className="mt-12 flex flex-wrap gap-2">
          {["All", "India", "International"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`filter-${f.toLowerCase()}`}
              className={`px-6 py-3 border text-[11px] uppercase tracking-[0.25em] transition-all duration-300 ${
                filter === f
                  ? "bg-foreground text-background border-foreground"
                  : "border-border text-foreground/70 hover:text-foreground hover:border-foreground"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </section>

      {/* Grid */}
      <section className="pb-32 container-luxe">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
          {filtered.map((d, i) => (
            <Reveal key={d.slug} delay={i * 60}>
              <Link to={`/destinations/${d.slug}`} className="group block" data-testid={`dest-list-${d.slug}`}>
                <div className="aspect-[4/5] overflow-hidden">
                  <img src={d.hero} alt={d.name} className="h-full w-full object-cover transition-transform duration-[1500ms] ease-out group-hover:scale-105" />
                </div>
                <div className="mt-6">
                  <div className="eyebrow text-foreground/50 mb-2">{d.region}</div>
                  <h3 className="font-serif text-3xl md:text-4xl tracking-tight group-hover:text-accent transition-colors">{d.name}</h3>
                  <div className="mt-1 text-foreground/60 italic font-serif text-lg">{d.tagline}</div>
                  <p className="mt-4 text-sm text-foreground/65 leading-relaxed max-w-md">{d.short}</p>
                  <div className="mt-5 flex items-center justify-between text-[11px] uppercase tracking-[0.25em] text-foreground/55">
                    <span>From {d.price_from}</span>
                    <span>{d.nights} Nights</span>
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}
